import { localStorage } from 'dibs-browser-storage';
import {
    STORAGE_KEY_SOURCING_PDP_VISITS,
    UNIQUE_PDP_VIEW_COUNT,
    TWO_WEEKS_PERIOD,
    SOURCING_MIN_PRICE,
    pdpInfoType,
    pdpType,
} from './pdpSharedConsumerSourcingHelpersConstants';
import { getUserPdpVisitsInfo } from './pdpSharedConsumerSourcingPdpInfoHelper';

export const removeUserPdpVisitsInfo: ({ userId }: { userId: string }) => pdpType[] = ({
    userId,
}) => {
    const { userPdpVisitsInfo, userIndex } = getUserPdpVisitsInfo({ userId });
    userPdpVisitsInfo.splice(userIndex, 1);
    return userPdpVisitsInfo;
};

/**
 * Gets user pdp visits information, and filters out last two weeks pdp recotds
 * @param {string} userId
 * @returns {pdpType[]} userPdpVisitsInfo
 * @returns {number} userIndex
 * @returns {pdpInfoType[]} userPdpCurrentRecords
 */

const getUserPdpVisits = ({
    userId,
}: {
    userId: string;
}): { userPdpVisitsInfo: pdpType[]; userIndex: number; userPdpCurrentRecords: pdpInfoType[] } => {
    const { userPdpVisitsInfo, userIndex } = getUserPdpVisitsInfo({ userId });
    const currentTime = Date.now();

    if (userIndex === -1) {
        return { userPdpVisitsInfo: [], userIndex, userPdpCurrentRecords: [] };
    } else {
        const userPdp = userPdpVisitsInfo?.[userIndex];
        // remove any pdp that was visited outside the two weeks window
        const userPdpCurrentRecords = userPdp.pdpInfo.filter(
            (pdp: pdpInfoType) => currentTime - pdp.lastView <= TWO_WEEKS_PERIOD
        );
        return { userPdpVisitsInfo, userIndex, userPdpCurrentRecords };
    }
};

/**
 * Updates user pdp visits information
 * @param {string} userId
 * @param {string} itemId
 * @param {number} itemPrice
 */
export const logSourcingPdpVisit = ({
    itemId,
    userId,
    itemPrice,
}: {
    itemId: string;
    userId: string;
    itemPrice: number;
}): void => {
    if (!itemPrice || itemPrice < SOURCING_MIN_PRICE) {
        return;
    }
    const { userPdpVisitsInfo, userIndex, userPdpCurrentRecords } = getUserPdpVisits({
        userId,
    });
    const currentTime = Date.now();

    if (userIndex === -1) {
        // if it is the first time a user has seen a pdp eligible for consumer sourcing modal
        userPdpVisitsInfo.push({
            userId: userId,
            pdpInfo: [{ itemId, itemPrice, count: 1, lastView: currentTime }],
        });
        localStorage.setItem(STORAGE_KEY_SOURCING_PDP_VISITS, userPdpVisitsInfo);
    } else {
        const userPdp = userPdpVisitsInfo.splice(userIndex, 1)[0];
        const pdpIndex = userPdpCurrentRecords.findIndex(
            (pdp: pdpInfoType) => pdp?.itemId === itemId
        );

        // if the pdp in view has been visited by the user before, increment the count but do not create new entry
        if (pdpIndex !== -1) {
            const pdp = userPdpCurrentRecords.splice(pdpIndex, 1)[0];
            userPdp.pdpInfo = [
                ...userPdpCurrentRecords,
                {
                    ...pdp,
                    count: pdp.count + 1,
                    lastView: currentTime,
                },
            ];
        } else {
            // if the pdp has not been visited by the user before, add the info the pdpInfo of the user
            userPdp.pdpInfo = [
                ...userPdpCurrentRecords,
                {
                    itemId,
                    itemPrice,
                    count: 1,
                    lastView: currentTime,
                },
            ];
        }
        userPdpVisitsInfo.push(userPdp);
        localStorage.setItem(STORAGE_KEY_SOURCING_PDP_VISITS, userPdpVisitsInfo);
    }
};

/**
 * Determines whether a consumer user has reached the 25th unique pdp that has a value over 5000USD
 * An example value would look like [{"userId":"12345","pdpInfo":[{"itemId":"f_12345"}]}]
 * @param {string} userId
 * @returns {boolean}
 */
export const evaluatePdpVisit = ({ userId }: { userId: string }): boolean => {
    const { userPdpCurrentRecords } = getUserPdpVisits({ userId });
    return userPdpCurrentRecords.length >= UNIQUE_PDP_VIEW_COUNT;
};
